import { FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslate } from 'hooks/useTranslate';
import { Modal } from 'react-bootstrap';
import { DecoupledEditor } from 'ckeditor5';

import { ScreenSizes } from 'constants/sizes';

import Button from 'modules/_shared/components/Buttons';

import { setModal } from 'modules/_shared/redux/modalActions';
import { Council } from 'types';
import { generateCouncilTemplates } from 'modules/administratives/utils/generateCouncilTemplates';
import { State } from 'redux/initialState';
import { saveCouncilAnnouncement } from 'modules/administratives/utils/saveCouncilAnnouncement';
import { store } from 'redux/store';
import { updateCouncil } from 'modules/administratives/redux/councilActions';
import CustomLoading from 'modules/_shared/components/CustomLoading';
import { addAlert } from 'modules/_shared/redux/alertActions';
import alertBodyTypes from 'modules/_shared/components/Alert/alertBodyTypes';
import CKEditor from 'modules/_shared/components/CKEditor';

type EditCouncilAnnouncementEditorProps = {
  size?: ScreenSizes;
  council: Council;
};

const EditCouncilAnnouncementEditor: FC<EditCouncilAnnouncementEditorProps> = ({
  council,
}) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();

  const user = useSelector((state: State) => state.user);
  const society = useSelector((state: State) => state.society.actualSociety);

  // State for CKEditor instance
  const [isLoading, setIsLoading] = useState(false);
  const [editorInstance, setEditorInstance] = useState<DecoupledEditor | null>(
    null
  );
  const [editorContent, setEditorContent] = useState(
    council?.announcementText || ''
  );
  const [hideEmailTemplate, setHideEmailTemplate] = useState(false);

  const handleEditorChange = () => {
    if (editorInstance) {
      const content = editorInstance.getData();
      setEditorContent(content);
    }
  };

  const handleClose = () => {
    dispatch(setModal(null));
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const templates = await generateCouncilTemplates({ user, society });
      const { announcementTemplate } = templates;

      const announcementDocumentId = await saveCouncilAnnouncement({
        user,
        society,
        council,
        announcementTemplate,
      });

      const newCouncilData = {
        _id: council._id,
        announcementText: editorContent,
        announcementTemplate,
        announcementDocument: announcementDocumentId
          ? announcementTemplate['_id']
          : null,
      };

      await store.dispatch(updateCouncil(newCouncilData));
      await store.dispatch(addAlert(alertBodyTypes.COUNCIL_UPDATED));

      dispatch(setModal(null));
    } catch (error) {
      await store.dispatch(addAlert(alertBodyTypes.ERROR_UPDATING_COUNCIL));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Modal.Header>
        <h4 className="modal-title mb-0">
          <span>{t('EditCouncilAnnouncement')}</span>
        </h4>

        <a className="close cursor-pointer" onClick={handleClose}>
          <em className="icon ni ni-cross" />
        </a>
      </Modal.Header>

      <Modal.Body className="custom-modal-body">
        <div className="nk-modal">
          <p className="text-muted mb-3">
            {t('EditCouncilAnnouncementEditorDescription')}
          </p>

          <div
            className="editor-compact-version"
            style={{ width: '100%', height: '300px !important' }}
          >
            <CKEditor
              editorInstance={editorInstance}
              setEditorInstance={setEditorInstance}
              documentName="Announcement"
              documentModel={editorContent}
              enableMentions={false}
              isReadOnly={false}
              onChangeContent={handleEditorChange}
              isCompactVersion
            />
          </div>

          {isLoading && <CustomLoading />}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="primary" onClick={handleSave}>
          {t('Save')}
        </Button>
      </Modal.Footer>
    </>
  );
};

export default EditCouncilAnnouncementEditor;
